/**
 * Generic status grid — device-scoped paginated list of SmartLink status records.
 *
 * Server-backed via EntityGrid over odl-app/generic-statuses/ scoped to a
 * relay controller. Clicking a row fetches the full status record (with measurements)
 * from the legacy endpoint via the status Redux slice, then calls onSelectedChange
 * so the parent panel (GenericStatusDetail) can render it.
 *
 * Contains:
 *  - GenericStatusGrid — default export
 */

import { Box } from '@mui/material';
import { addMonths } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import TimeRangeSelector from '@sophocles/components/TimeRangeSelector';
import { EntityGrid } from '@sophocles/data-grids';
import { loadStatus } from '@sophocles/features/status/api';
import { selectStatusById } from '@sophocles/features/status/slice';

function GenericStatusGrid({ relayControllerId, onSelectedChange }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [selectedId, setSelectedId] = useState(null);
  const selectedStatus = useSelector(state => selectStatusById(state, selectedId));

  const [startDate, setStartDate] = useState(() => addMonths(new Date(), -1));
  const [endDate, setEndDate] = useState(() => new Date());

  const handleDateChange = useCallback((start, end) => {
    if (start) setStartDate(start);
    if (end) setEndDate(end);
  }, []);

  // When the Redux slice updates with the full status record (measurements
  // loaded by loadStatus on row click), propagate to the parent detail panel.
  useEffect(() => {
    if (onSelectedChange) onSelectedChange(selectedStatus);
  }, [selectedStatus, onSelectedChange]);

  // Bound the query by date. Use raw timestamp bounds (not a __date cast) to
  // let Postgres prune monthly partitions of pliny_generic_status. The
  // registered filter param is last_receive_time (datetime); dot-suffix form
  // required by the odl-app grid framework.
  const extraParams = useMemo(() => {
    const p = {};
    if (relayControllerId) p.relay_controller_id = relayControllerId;
    if (startDate) p['last_receive_time.gte'] = startDate.toISOString();
    if (endDate) p['last_receive_time.lt'] = endDate.toISOString();
    return p;
  }, [relayControllerId, startDate, endDate]);

  // Row click: select the row and fetch its full record (the list endpoint
  // returns a slim shape without measurements; the detail panel loads the full
  // record on demand). params.row.id is the real server ID even in infinite
  // scroll mode (EntityGrid adds _gridIdx but preserves all server fields).
  const handleRowClick = useCallback(
    params => {
      setSelectedId(params.row.id);
      dispatch(loadStatus({ relayControllerId, statusId: params.row.id }));
    },
    [dispatch, relayControllerId],
  );

  const fieldOverrides = useMemo(
    () => ({
      last_receive_time: {
        valueFormatter: value => (value ? new Date(value).toLocaleString() : ''),
      },
    }),
    [],
  );

  const toolbarContent = useMemo(
    () => (
      <TimeRangeSelector
        startDate={startDate}
        endDate={endDate}
        onChange={handleDateChange}
        size="sm"
        exclude={['last1h', 'last24h']}
        defaultPreset="last30d"
      />
    ),
    [startDate, endDate, handleDateChange],
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '70vh', minHeight: 360 }}>
      <EntityGrid
        basePath="generic-statuses"
        fieldOverrides={fieldOverrides}
        extraParams={extraParams}
        options={{
          showSearch: false,
          infiniteScroll: true,
          toolbarContent,
          onRowClick: handleRowClick,
          gridProps: {
            density: 'compact',
            disableColumnFilter: true,
            disableColumnSelector: true,
            hideFooter: true,
            sx: {
              flex: 1,
              minHeight: 0,
              '& .MuiDataGrid-row': { cursor: 'pointer' },
            },
          },
        }}
      />
    </Box>
  );
}

export default GenericStatusGrid;
